<template>
    <ScrollableView class="open-bills-view-container">
        <div class="open-bills-view">
            <!-- <OpenBillsList
                v-if="!isLoading && !isReallocated"
                ref="openBillsListComponent"
                :isMobileView="true"
                :disableCurrentTable="true"
                :billListType="BillListType.OrdersForNowOnly"
                @tableSelected="allocateToTable"
                @openBillsLoadingError="onBillsLoadingError"
            />
            <div class="loading-placeholder" v-if="isLoading">{{ translations.openBills.loadingData }}</div> -->

            <TabView class="open-bills-tab-view" @tabChange="onTabChange">
                <TabPanel contentClass="open-bills-tab">
                    <template v-slot:header>
                        {{ translations.openBills.ordersForNow }} ({{ orderForNowBillsCount }})
                    </template>

                    <OpenBillsList
                        v-if="!isLoading && !isReallocated"
                        ref="openOrderForNowBillsListComponent"
                        :isMobileView="true"
                        :disableCurrentTable="true"
                        :billListType="BillListType.OrdersForNowOnly"
                        @tableSelected="allocateToTable"
                        @openBillsLoadingError="onBillsLoadingError"
                    />
                    <div class="loading-placeholder" v-if="isLoading">{{ translations.openBills.loadingData }}</div>
                </TabPanel>
                <TabPanel contentClass="open-bills-tab">
                    <template v-slot:header>
                        {{ translations.openBills.ordersForNowOrLater }} ({{ orderForNowOrLaterBillsCount }})
                    </template>

                    <OpenBillsList
                        v-if="!isLoading && !isReallocated"
                        ref="openOrderForNowOrLaterBillsListComponent"
                        :isMobileView="true"
                        :disableCurrentTable="true"
                        :billListType="BillListType.OrdersForNowOrLaterOnly"
                        @tableSelected="allocateToTable"
                        @openBillsLoadingError="onBillsLoadingError"
                    />
                    <div class="loading-placeholder" v-if="isLoading">{{ translations.openBills.loadingData }}</div>
                </TabPanel>
            </TabView>
        </div>
    </ScrollableView>
</template>

<script setup lang="ts">
    import { TastierClient } from "@/app/TastierClient";
    import { Popup } from "@/app/ui/Popup";
    import { dataEvents } from "@/data/events/dataEvents";
    import type { OpenBillSelectedEventData } from "@/data/model/Bill";
    import { BillListType } from "@/data/model/Enums";
    import { CurrentLocationService } from "@/data/services/CurrentLocationService";
    import { Logging } from "@/data/util/Logging";
    import { translations } from "@/shared/localization/translations";
    import { logError } from "@/shared/logging/errors";
    import { computed, nextTick, onBeforeUnmount, onMounted, ref } from "vue";

    import OpenBillsList from "@/components/bill/OpenBillsList.vue";
    import ScrollableView from "@/components/layout/ScrollableView.vue";
    import TabPanel from "primevue/tabpanel";
    import TabView from "primevue/tabview";

    // state
    const isLoading = ref(false);
    const isReallocated = ref(false);
    const isRefreshingOpenBills = ref(false);
    const isOpenBillsRefreshRequested = ref(false);

    const orderForNowBillsCount = computed(() => openOrderForNowBillsListComponent.value?.openBillsCount ?? 0);
    const orderForNowOrLaterBillsCount = computed(
        () => openOrderForNowOrLaterBillsListComponent.value?.openBillsCount ?? 0
    );

    // define html refs
    const openOrderForNowBillsListComponent = ref<InstanceType<typeof OpenBillsList> | null>(null);
    const openOrderForNowOrLaterBillsListComponent = ref<InstanceType<typeof OpenBillsList> | null>(null);

    // functions
    async function allocateToTable(eventData: OpenBillSelectedEventData) {
        isLoading.value = true;
        isReallocated.value = false;
        try {
            const allocationSuccess = await TastierClient.context.allocate(
                eventData.locationId,
                eventData.tableNumber,
                undefined,
                false,
                eventData.selectedOpenBill.id
            );
            // when allocate function is successful, the user will be redirected to main page by 'onAllocationChange' event
            isReallocated.value = allocationSuccess;
        } catch (ex) {
            logError(ex);
            Popup.show(translations.allocation.allocationFailed);
        } finally {
            isLoading.value = false;
        }
    }

    async function refreshOpenBillsList() {
        if (CurrentLocationService.getLocationId() == null) {
            Logging.LogWarning("User not allocated. Skipping bills refresh.");
            return;
        }

        // if is already refreshing - enqueue next request and leave
        if (isRefreshingOpenBills.value == true) {
            isOpenBillsRefreshRequested.value = true;
            return;
        }

        // if is not refreshing - start refreshing
        // first reset previously set flag - if it was set to true
        isOpenBillsRefreshRequested.value = false;

        // start refreshing
        try {
            await openOrderForNowBillsListComponent.value?.refresh();
            await openOrderForNowOrLaterBillsListComponent.value?.refresh();
        } finally {
            isRefreshingOpenBills.value = false;
        }

        // if there is a new refresh request enqueued - refresh again - after 1 seconds
        if (isOpenBillsRefreshRequested.value) {
            setTimeout(refreshOpenBillsList, 1000);
        }
    }

    function onBillsLoadingError() {
        Popup.show(translations.openBills.failedLoadingBills);
    }

    async function onTabChange() {
        await nextTick();
        await openOrderForNowBillsListComponent.value?.redraw();
        await nextTick();
        await openOrderForNowOrLaterBillsListComponent.value?.redraw();
    }

    // register events
    onMounted(async () => {
        dataEvents.appSocket.BillOpened.add(refreshOpenBillsList);
        dataEvents.appSocket.BillChanged.add(refreshOpenBillsList);
        dataEvents.appSocket.BillClosed.add(refreshOpenBillsList);
        dataEvents.appSocket.BillNameChanged.add(refreshOpenBillsList);
        dataEvents.appSocket.BillManualEndUsersCountChanged.add(refreshOpenBillsList);
        dataEvents.appSocket.TableCovidUserCountChanged.add(refreshOpenBillsList);
    });

    onBeforeUnmount(() => {
        dataEvents.appSocket.BillOpened.remove(refreshOpenBillsList);
        dataEvents.appSocket.BillChanged.remove(refreshOpenBillsList);
        dataEvents.appSocket.BillClosed.remove(refreshOpenBillsList);
        dataEvents.appSocket.BillNameChanged.remove(refreshOpenBillsList);
        dataEvents.appSocket.BillManualEndUsersCountChanged.remove(refreshOpenBillsList);
        dataEvents.appSocket.TableCovidUserCountChanged.remove(refreshOpenBillsList);
    });
</script>

<style lang="scss">
    .open-bills-view-container {
        position: absolute !important;
        top: 53px;
        left: 0;
        right: 0;
        bottom: 0;

        .open-bills-view {
            > .loading-placeholder {
                text-align: center;
                padding: 1.5em 2em;
                line-height: 2em;
            }
        }
    }
</style>
